import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, ChevronDown, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Discreet admin login portal for the site footer.
 *
 * Collapsed by default (a small "Admin login" pill). Clicking expands into
 * an inline form. Hits `/api/auth/admin-login` — only admins pass; clients /
 * unknown emails get a friendly hint without leaking existence.
 *
 * On success: stores the JWT in localStorage (same key AdminHub reads) and
 * routes to /admin.
 */
const FooterAdminLogin = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const wrapperRef = useRef(null);
  const emailRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // Focus email on open
    setTimeout(() => emailRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const em = email.trim().toLowerCase();
    if (!em || !password) return toast.error("Email and password required.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/admin-login`, {
        email: em,
        password,
      });
      localStorage.setItem("sb_admin_token", data.access_token);
      toast.success(`Welcome, ${data.user.name || em.split("@")[0]}.`);
      setOpen(false);
      setPassword("");
      navigate("/admin");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("This portal is for admin accounts only.");
      } else if (status === 429) {
        toast.error("Too many attempts. Try again in a few minutes.");
      } else {
        toast.error("Invalid email or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative inline-block ${open ? "z-[95]" : ""}`} data-testid="footer-admin-login">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="footer-admin-login-toggle"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-white/35 hover:text-[#00f0ff] transition-colors group"
        >
          <Lock size={10} className="group-hover:text-[#00f0ff]" />
          Admin login
          <ChevronDown size={9} className="opacity-60 group-hover:opacity-100" />
        </button>
      ) : (
        <form
          onSubmit={submit}
          data-testid="footer-admin-login-form"
          className="absolute z-[95] bottom-full left-0 md:left-auto md:right-0 mb-3 w-[calc(100vw-2.5rem)] max-w-[320px] rounded-2xl border border-white/12 bg-[#0b0715]/95 backdrop-blur-md p-4 shadow-2xl shadow-black/50"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
              <ShieldCheck size={13} />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-[#00f0ff]">Command Center</div>
              <div className="text-sm font-semibold">Admin login</div>
            </div>
          </div>

          <input
            ref={emailRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@sbmediastudio.com"
            autoComplete="username"
            data-testid="footer-admin-email"
            className="w-full px-3 py-2 mb-2 rounded-lg bg-[#05050a] border border-white/15 text-sm placeholder-white/25 focus:outline-none focus:border-[#00f0ff]/60"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            data-testid="footer-admin-password"
            className="w-full px-3 py-2 mb-3 rounded-lg bg-[#05050a] border border-white/15 text-sm placeholder-white/25 focus:outline-none focus:border-[#00f0ff]/60"
          />

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] text-white/50 hover:text-white/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="footer-admin-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-xs hover:bg-white transition disabled:opacity-50"
            >
              {submitting ? <Loader2 size={11} className="animate-spin" /> : <ArrowRight size={11} />}
              Sign in
            </button>
          </div>

          <div className="text-[10px] text-white/35 mt-3">
            Admin-only. Non-admin logins won&apos;t be accepted here.
          </div>
        </form>
      )}
    </div>
  );
};

export default FooterAdminLogin;
